import { useEffect, useState } from 'react';
import {
  apiCreateTask,
  apiExportTaskUrl,
  apiListAdminTasks,
  apiPauseTask,
  apiResumeTask,
} from '../api';
import type { TaskRow } from '../types';
import { fmtTime } from '../lib/format';

interface Props {
  selectedTaskId: number | null;
  onSelectTask: (id: number | null) => void;
}

/**
 * Top section of the Review page:
 *   - Create new task (collapsed form by default)
 *   - List all tasks (active + paused) with select / pause-resume / export actions
 *
 * Selecting a task lifts the id to the parent, which scopes the event table
 * below to that task. Selecting "全部" clears the scope.
 */
export default function TaskManager({ selectedTaskId, onSelectTask }: Props) {
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  // Create-form state
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [createMsg, setCreateMsg] = useState<string | null>(null);
  const [createErr, setCreateErr] = useState<string | null>(null);

  async function loadTasks() {
    try {
      const r = await apiListAdminTasks();
      setTasks(r.tasks);
    } catch (e) {
      setErr(String(e));
    }
  }
  useEffect(() => { loadTasks(); }, []);

  async function togglePause(t: TaskRow) {
    setBusyId(t.id);
    try {
      if (t.paused) await apiResumeTask(t.id);
      else await apiPauseTask(t.id);
      await loadTasks();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateMsg(null); setCreateErr(null);
    if (!file) { setCreateErr('請選擇 JSON 檔案'); return; }
    if (!name.trim()) { setCreateErr('請輸入名稱'); return; }
    setCreateBusy(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) throw new Error('JSON 必須為事件陣列');
      const events = parsed.filter(
        (e: any) => typeof e?.event_id === 'number' && typeof e?.video_url === 'string',
      );
      if (events.length === 0) throw new Error('找不到有效事件 (需 event_id 與 video_url)');
      const r = await apiCreateTask({
        name: name.trim(),
        description: description.trim() || undefined,
        events,
      });
      setCreateMsg(`已建立任務 #${r.task_id}:成功插入 ${r.inserted} / ${r.submitted} 筆事件`);
      setName(''); setDescription(''); setFile(null);
      setShowCreate(false);
      await loadTasks();
    } catch (e) {
      setCreateErr(String(e));
    } finally {
      setCreateBusy(false);
    }
  }

  const selected = tasks.find(t => t.id === selectedTaskId) ?? null;

  return (
    <section className="task-manager">
      <div className="task-manager-header">
        <h3>任務</h3>
        <div className="row">
          {selected && (
            <span className="muted small">
              目前已選取:#{selected.id} {selected.name}
              {' '}
              <button className="linkish" onClick={() => onSelectTask(null)}>清除</button>
            </span>
          )}
          <button onClick={() => setShowCreate(s => !s)}>
            {showCreate ? '收合' : '+ 新增任務'}
          </button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      {showCreate && (
        <form onSubmit={submitCreate} className="stacked task-create-form">
          <label>
            名稱
            <input value={name} onChange={e => setName(e.target.value)} required />
          </label>
          <label>
            描述
            <input value={description} onChange={e => setDescription(e.target.value)} />
          </label>
          <label>
            JSON 檔案 (事件陣列,需包含 event_id 與 video_url)
            <input
              type="file"
              accept="application/json,.json"
              onChange={e => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <div className="row">
            <button type="submit" disabled={createBusy}>
              {createBusy ? '上傳中…' : '建立任務'}
            </button>
            <button type="button" className="linkish" onClick={() => setShowCreate(false)}>
              取消
            </button>
          </div>
          {createMsg && <div className="ok">{createMsg}</div>}
          {createErr && <div className="error">{createErr}</div>}
        </form>
      )}

      <table className="grid">
        <thead>
          <tr>
            <th>ID</th>
            <th>名稱</th>
            <th>事件數</th>
            <th>未完成</th>
            <th>建立時間</th>
            <th>狀態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map(t => {
            const remaining = t.event_count - t.labeled_count;
            const isSelected = t.id === selectedTaskId;
            return (
              <tr key={t.id} className={isSelected ? 'row-active' : ''}>
                <td>{t.id}</td>
                <td>{t.name}</td>
                <td>{t.event_count}</td>
                <td>{remaining}</td>
                <td>{fmtTime(t.created_at)}</td>
                <td>
                  {t.paused ? (
                    <span className="badge badge-deleted">暫停</span>
                  ) : (
                    <span className="badge badge-active">啟用</span>
                  )}
                </td>
                <td>
                  <button
                    className="linkish"
                    onClick={() => onSelectTask(isSelected ? null : t.id)}
                  >
                    {isSelected ? '取消選取' : '選取'}
                  </button>
                  {' · '}
                  <button
                    className="linkish"
                    disabled={busyId !== null}
                    onClick={() => togglePause(t)}
                  >
                    {busyId === t.id ? '…' : t.paused ? '恢復' : '暫停'}
                  </button>
                  {' · '}
                  <a href={apiExportTaskUrl(t.id)} target="_blank" rel="noreferrer">
                    匯出
                  </a>
                </td>
              </tr>
            );
          })}
          {tasks.length === 0 && (
            <tr><td colSpan={7}>尚無任務</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}
